import React, { useState, useEffect } from "react";
import {
  HiOutlineCheckCircle,
  HiOutlineDocumentText,
  HiOutlineShieldCheck,
  HiOutlineExclamationTriangle,
  HiOutlineUser,
  HiOutlineXMark,
  HiOutlineChevronDown,
  HiOutlineArrowTopRightOnSquare,
  HiOutlineBriefcase,
  HiOutlinePhoto,
  HiOutlineTrash,
  HiOutlineAcademicCap,
  HiOutlineSparkles,
} from "react-icons/hi2";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Panel, FormButton } from "../../ui";
import { canUseAutoBid, canUseBiddingSettings } from "../../util/plan-gates";
import { useUser } from "../../hooks";
import {
  IUpworkProfile,
  IPrompt,
  IAutoBidSettings,
  IProfileHighlight,
  APP_ROUTES,
} from "../../data-access";
import { researcherApi } from "../../api/job-researcher";
import { usersApi, promptsApi, settingsApi } from "../../api";
import { storeAuthActions } from "../../store/slices/auth";
import { syncAuthWithExtension } from "../../util/auth-sync/auth-sync";
import { notify } from "../../util/notify/notify";
import "./ai-settings.scss";


// ── Profile Highlights types/helpers ──

interface IHlSourceItem {
  id: string;
  title: string;
  description?: string;
  // job
  rating?: number;
  dateRange?: string;
  price?: string;
  // portfolio
  skills?: string[];
  thumbnail?: string;
  // certificate
  provider?: string;
  issued?: string;
}

const HL_TYPE_LABEL: Record<IProfileHighlight["type"], string> = {
  job: "Completed Upwork job",
  portfolio: "Portfolio",
  certificate: "Certificate",
};

// ── Component ──

export const AISettings = (): JSX.Element => {
  const { currentUser } = useUser();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Real profiles from API
  const [profiles, setProfiles] = useState<IUpworkProfile[]>([]);
  const [profilesLoading, setProfilesLoading] = useState(true);
  const [switching, setSwitching] = useState(false);

  useEffect(() => {
    researcherApi
      .getUpworkProfiles()
      .then((res: any) => {
        setProfiles(
          (res?.data || []).filter(
            (p: IUpworkProfile) => p.username && p.userId
          )
        );
      })
      .catch(() => {})
      .finally(() => setProfilesLoading(false));
  }, []);

  // Cover letter templates from API
  const [coverTemplates, setCoverTemplates] = useState<IPrompt[]>([]);
  const [coverTemplatesLoading, setCoverTemplatesLoading] = useState(true);
  const [templateSaving, setTemplateSaving] = useState(false);

  useEffect(() => {
    promptsApi
      .getPrompts()
      .then((res) => setCoverTemplates(res?.data || []))
      .catch(() => {})
      .finally(() => setCoverTemplatesLoading(false));
  }, []);

  // Active profile — match by currentUser.upwork_profile.userId
  const activeUserId = currentUser?.upwork_profile?.userId;
  const upworkProfile =
    profiles.find((p) => p.userId === activeUserId) ||
    profiles.find((p) => p.is_selected) ||
    profiles[0] ||
    null;
  const activeProfileId = upworkProfile?.id ?? 0;

  // Switch active profile
  const handleSwitchProfile = async (profile: IUpworkProfile) => {
    setProfileDropdownOpen(false);
    setProfileModalOpen(false);
    if (profile.id === activeProfileId) return;
    setSwitching(true);
    try {
      await researcherApi.makeProfileActive(profile.id);
      const meRes = await usersApi.getMe();
      dispatch(storeAuthActions.setUserData({ user: meRes.data }));
      // Refresh profiles to update is_selected flags
      const profilesRes = await researcherApi.getUpworkProfiles();
      setProfiles(profilesRes?.data || []);
    } catch (e) {
      console.error("Failed to switch profile:", e);
    } finally {
      setSwitching(false);
    }
  };

  // Default prompts (used when backend has no saved value)
  const DEFAULT_MATCH_PROMPT = `Only select jobs that align with the skills listed in my Upwork profile. The job should require at least some of my core skills or technologies.

Prioritize jobs where:
- Required skills overlap with my profile skills
- Job category matches my expertise area
- Experience level fits my profile
- Budget is reasonable for the scope described

Decline jobs that have zero overlap with my skillset or are clearly outside my field.`;
  const DEFAULT_RED_FLAGS_PROMPT = `Reject the job if ANY of these apply:

- Client has no payment method verified
- Client rating is below 4.0 stars
- Client has never hired anyone before (total spent is $0)
- Budget is significantly below market rate for the described scope
- Job description is vague, too short (under 50 words), or looks like spam
- Job requires unpaid test work or "free trial" period
- Job has 50+ proposals already (too competitive)

Also be cautious if:
- Description mentions multiple unrelated skills (scope creep)
- Timeline is unrealistic for the budget`;

  const [matchPrompt, setMatchPrompt] = useState<string>(DEFAULT_MATCH_PROMPT);
  const [redFlagsPrompt, setRedFlagsPrompt] = useState<string>(
    DEFAULT_RED_FLAGS_PROMPT
  );

  // Editing state
  const PROMPT_MAX_LENGTH = 5000;
  const [editingPrompt, setEditingPrompt] = useState<string | null>(null);
  const [editValue, setEditValue] = useState("");
  const [promptSaving, setPromptSaving] = useState(false);
  const [promptError, setPromptError] = useState<string | null>(null);

  // Auto-bid settings
  const AB_DEFAULTS: IAutoBidSettings = {
    autobid_cover_letter_template_id: null,
    autobid_hourly_rate: 30,
    autobid_rate_increase_frequency: "never",
    autobid_rate_increase_percentage: 5,
    autobid_fixed_price_adjustment: 0,
    autobid_project_duration: "automatic",
    autobid_match_prompt: null,
    autobid_red_flags_prompt: null,
    autobid_max_bids_per_day: 25,
    autobid_max_connects_per_day: 200,
    autobid_stop_on_low_connects: 50,
    autobid_scan_interval: "normal",
    autobid_jobs_per_page: 20,
    autobid_job_age_limit: "any",
    autobid_min_match_score: 60,
    autobid_skip_fixed_price: false,
    autobid_skip_hourly: false,
    autobid_active_hours_from: null,
    autobid_active_hours_to: null,
    autobid_timezone: null,
    autobid_highlights_mode: "off",
    autobid_highlights_selection: [],
    // Smart Boosting відкладено (не завершено):
    // autobid_smart_boost: false,
    // autobid_smart_boost_max_connects: 50,
  };
  const [autoBidSettings, setAutoBidSettings] =
    useState<IAutoBidSettings>(AB_DEFAULTS);
  const [abSettingsLoading, setAbSettingsLoading] = useState(true);
  const [abSettingsSaving, setAbSettingsSaving] = useState(false);
  const [abErrors, setAbErrors] = useState<Record<string, string>>({});
  const timeFromRef = React.useRef<HTMLInputElement>(null);
  const timeToRef = React.useRef<HTMLInputElement>(null);

  useEffect(() => {
    settingsApi
      .getSettings()
      .then((res) => {
        if (res?.data?.autobid) {
          // Merge with defaults so fields the backend doesn't return yet
          // (e.g. new highlights fields) don't become undefined.
          setAutoBidSettings({ ...AB_DEFAULTS, ...res.data.autobid });
          if (res.data.autobid.autobid_match_prompt) {
            setMatchPrompt(res.data.autobid.autobid_match_prompt);
          }
          if (res.data.autobid.autobid_red_flags_prompt) {
            setRedFlagsPrompt(res.data.autobid.autobid_red_flags_prompt);
          }
        }
      })
      .catch(() => {})
      .finally(() => setAbSettingsLoading(false));
  }, []);

  // Use profile hourly rate as default when settings still have the DB default (30)
  useEffect(() => {
    if (
      !abSettingsLoading &&
      upworkProfile?.hourly_rate &&
      autoBidSettings.autobid_hourly_rate === 30
    ) {
      setAutoBidSettings((prev) => ({
        ...prev,
        autobid_hourly_rate: upworkProfile.hourly_rate!,
      }));
    }
  }, [upworkProfile, abSettingsLoading, autoBidSettings.autobid_hourly_rate]);

  const validateAutoBidSettings = (): Record<string, string> => {
    const errors: Record<string, string> = {};
    const s = autoBidSettings;
    if (!s.autobid_hourly_rate || s.autobid_hourly_rate < 1)
      errors.autobid_hourly_rate = "Must be at least $1";
    if (!s.autobid_max_bids_per_day || s.autobid_max_bids_per_day < 1)
      errors.autobid_max_bids_per_day = "Must be at least 1";
    if (!s.autobid_max_connects_per_day || s.autobid_max_connects_per_day < 10)
      errors.autobid_max_connects_per_day = "Must be at least 10";
    if (s.autobid_stop_on_low_connects < 0)
      errors.autobid_stop_on_low_connects = "Cannot be negative";
    if (!s.autobid_min_match_score || s.autobid_min_match_score < 40)
      errors.autobid_min_match_score = "Must be at least 40";
    if (s.autobid_min_match_score > 90)
      errors.autobid_min_match_score = "Cannot exceed 90";
    // Active Hours: check for incomplete time input via DOM validity
    if (timeFromRef.current?.validity.badInput)
      errors.autobid_active_hours_from = "Enter a complete time (HH:MM)";
    if (timeToRef.current?.validity.badInput)
      errors.autobid_active_hours_to = "Enter a complete time (HH:MM)";
    return errors;
  };

  const handleSaveAutoBidSettings = async () => {
    const validationErrors = validateAutoBidSettings();
    if (Object.keys(validationErrors).length > 0) {
      setAbErrors(validationErrors);
      notify("error", "Please fix the highlighted fields");
      return;
    }
    setAbErrors({});
    setAbSettingsSaving(true);
    try {
      const payload = {
        ...autoBidSettings,
        autobid_match_prompt: matchPrompt,
        autobid_red_flags_prompt: redFlagsPrompt,
      };
      await settingsApi.updateAutoBidSettings(payload);
      syncAuthWithExtension("AUTOBID_SETTINGS_UPDATE", payload);
      notify("success", "Settings saved");
    } catch (e: any) {
      // Parse backend validation errors
      const backendErrors = e?.response?.data?.errors || e?.errors;
      if (backendErrors && typeof backendErrors === "object") {
        const parsed: Record<string, string> = {};
        for (const [key, val] of Object.entries(backendErrors)) {
          parsed[key] = Array.isArray(val) ? val[0] : String(val);
        }
        setAbErrors(parsed);
        notify("error", "Please fix the highlighted fields");
      } else {
        notify("error", "Failed to save settings");
      }
    } finally {
      setAbSettingsSaving(false);
    }
  };

  const updateAB = (field: keyof IAutoBidSettings, value: any) => {
    setAutoBidSettings((prev) => ({ ...prev, [field]: value }));
    if (abErrors[field]) {
      setAbErrors((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      });
    }
  };

  // ── Profile Highlights (v4.1.0) ──
  // Available highlights = snapshot of the synced profile, served by the backend.
  const [highlightsSource, setHighlightsSource] = useState<
    Record<IProfileHighlight["type"], IHlSourceItem[]>
  >({ job: [], portfolio: [], certificate: [] });
  const [highlightsLoading, setHighlightsLoading] = useState(true);

  useEffect(() => {
    if (!activeProfileId) {
      if (!profilesLoading) setHighlightsLoading(false);
      return;
    }
    setHighlightsLoading(true);
    researcherApi
      .getProfileHighlights(activeProfileId)
      .then((res) => {
        const fmtDate = (d: string | null | undefined): string => {
          if (!d) return "";
          const parsed = new Date(d);
          return isNaN(parsed.getTime())
            ? d
            : parsed.toLocaleDateString("en-US", {
                month: "short",
                year: "numeric",
              });
        };
        const fmtPrice = (j: {
          amount: number | null;
          currency: string | null;
          paymentType: string | null;
        }): string => {
          const amount =
            j.amount != null
              ? `${j.currency === "USD" || !j.currency ? "$" : `${j.currency} `}${j.amount}`
              : "";
          return [amount, j.paymentType].filter(Boolean).join(" · ");
        };
        setHighlightsSource({
          job: (res?.jobs || []).map((j) => ({
            id: String(j.id),
            title: j.title,
            description: j.feedback || j.description || undefined,
            rating: j.rating ?? undefined,
            dateRange: [fmtDate(j.startedOn), fmtDate(j.endedOn)]
              .filter(Boolean)
              .join(" - "),
            price: fmtPrice(j),
          })),
          portfolio: (res?.portfolio || []).map((p) => ({
            id: String(p.id),
            title: p.title,
            description: p.description || undefined,
            thumbnail: p.thumbnail || undefined,
            skills: p.skills || [],
          })),
          certificate: (res?.certificates || []).map((c) => ({
            id: String(c.id),
            title: c.title,
            description: c.description || undefined,
            provider: c.provider || undefined,
            issued: c.issued || undefined,
          })),
        });
      })
      .catch(() => {})
      .finally(() => setHighlightsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProfileId, profilesLoading]);

  // Match by id; legacy selections saved without id fall back to title.
  const findHlSource = (
    type: IProfileHighlight["type"],
    h: { id?: string; title: string }
  ): IHlSourceItem | undefined =>
    highlightsSource[type]?.find((i) =>
      h.id ? i.id === h.id : i.title === h.title
    );

  // Selection items that no longer exist in the synced profile (profile changed
  // on Upwork without a re-sync) — shown as unavailable, silently skipped by
  // the extension.
  const isHlUnavailable = (h: IProfileHighlight): boolean =>
    !highlightsLoading && !findHlSource(h.type, h);
  const HL_TABS: { key: IProfileHighlight["type"]; label: string }[] = [
    { key: "job", label: "Upwork jobs" },
    { key: "portfolio", label: "Portfolio" },
    { key: "certificate", label: "Certificates" },
  ];
  const HL_MAX = 4;

  // Are there any highlights to choose from? (parsed from the synced profile;
  // mock for now). If none → warn + disable the mode selector.
  const hlHasAvailable =
    highlightsSource.job.length +
      highlightsSource.portfolio.length +
      highlightsSource.certificate.length >
    0;

  const [highlightsModalOpen, setHighlightsModalOpen] = useState(false);
  const [highlightsTab, setHighlightsTab] =
    useState<IProfileHighlight["type"]>("job");
  const [highlightsDraft, setHighlightsDraft] = useState<IProfileHighlight[]>(
    []
  );
  const [highlightsSaving, setHighlightsSaving] = useState(false);

  const hlKey = (h: { type: string; id?: string; title: string }) =>
    `${h.type}::${h.id || h.title}`;
  const hlSame = (
    h: { type: string; id?: string; title: string },
    type: IProfileHighlight["type"],
    item: { id: string; title: string }
  ) => h.type === type && (h.id ? h.id === item.id : h.title === item.title);
  const isHighlightSelected = (
    type: IProfileHighlight["type"],
    item: { id: string; title: string }
  ) => highlightsDraft.some((h) => hlSame(h, type, item));

  const toggleHighlight = (
    type: IProfileHighlight["type"],
    item: { id: string; title: string }
  ) => {
    setHighlightsDraft((prev) => {
      const exists = prev.some((h) => hlSame(h, type, item));
      if (exists) return prev.filter((h) => !hlSame(h, type, item));
      if (prev.length >= HL_MAX) return prev; // cap at 4
      return [...prev, { type, id: item.id, title: item.title }];
    });
  };

  const removeHighlight = (h: IProfileHighlight) =>
    setHighlightsDraft((prev) =>
      prev.filter((x) => !hlSame(x, h.type, { id: h.id, title: h.title }))
    );

  // Drag-to-reorder for the selected (right) list
  const [hlDragIndex, setHlDragIndex] = useState<number | null>(null);
  const moveHighlight = (from: number, to: number) => {
    if (from === to || from < 0 || to < 0) return;
    setHighlightsDraft((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const openHighlightsModal = () => {
    setHighlightsDraft(autoBidSettings.autobid_highlights_selection || []);
    setHighlightsTab("job");
    setHighlightsModalOpen(true);
  };

  // Smart Boosting відкладено (не завершено) - хелпери закоментовані:
  // const persistSmartBoostMax = () => {
  //   const raw = Number(autoBidSettings.autobid_smart_boost_max_connects);
  //   const clamped = Math.min(1000, Math.max(1, Math.floor(raw) || 1));
  //   setAutoBidSettings((prev) => ({
  //     ...prev,
  //     autobid_smart_boost_max_connects: clamped,
  //   }));
  //   settingsApi
  //     .updateAutoBidSettings({ autobid_smart_boost_max_connects: clamped })
  //     .then(() =>
  //       syncAuthWithExtension("AUTOBID_SETTINGS_UPDATE", {
  //         ...autoBidSettings,
  //         autobid_smart_boost_max_connects: clamped,
  //       })
  //     )
  //     .catch(() => {});
  // };
  //
  // const setSmartBoost = (enabled: boolean) => {
  //   setAutoBidSettings((prev) => ({ ...prev, autobid_smart_boost: enabled }));
  //   settingsApi
  //     .updateAutoBidSettings({ autobid_smart_boost: enabled })
  //     .then(() =>
  //       syncAuthWithExtension("AUTOBID_SETTINGS_UPDATE", {
  //         ...autoBidSettings,
  //         autobid_smart_boost: enabled,
  //       })
  //     )
  //     .catch(() => {});
  // };

  // Mode change auto-saves (silently), like the Cover Letter template select.
  const setHighlightsMode = (mode: IAutoBidSettings["autobid_highlights_mode"]) => {
    setAutoBidSettings((prev) => ({ ...prev, autobid_highlights_mode: mode }));
    settingsApi
      .updateAutoBidSettings({ autobid_highlights_mode: mode })
      .then(() =>
        syncAuthWithExtension("AUTOBID_SETTINGS_UPDATE", {
          ...autoBidSettings,
          autobid_highlights_mode: mode,
        })
      )
      .catch(() => {});
  };

  // Persist highlights settings (mode + selection). `override` lets callers pass
  // fresh values without waiting for setState to flush.
  const persistHighlights = async (override?: Partial<IAutoBidSettings>) => {
    setHighlightsSaving(true);
    try {
      const payload = {
        autobid_highlights_mode: autoBidSettings.autobid_highlights_mode,
        autobid_highlights_selection:
          autoBidSettings.autobid_highlights_selection || [],
        ...override,
      };
      await settingsApi.updateAutoBidSettings(payload);
      syncAuthWithExtension("AUTOBID_SETTINGS_UPDATE", {
        ...autoBidSettings,
        ...override,
      });
      notify("success", "Highlights saved");
      return true;
    } catch (e) {
      notify("error", "Failed to save highlights");
      return false;
    } finally {
      setHighlightsSaving(false);
    }
  };

  const renderHlThumb = (
    type: IProfileHighlight["type"],
    src?: IHlSourceItem
  ): JSX.Element => (
    <span className="aia-hl-thumb">
      {/* fallback icon (sits behind the image; shown if image is missing/fails) */}
      {type === "job" ? (
        <HiOutlineBriefcase size={20} />
      ) : type === "certificate" ? (
        <HiOutlineAcademicCap size={20} />
      ) : (
        <HiOutlinePhoto size={20} />
      )}
      {type === "portfolio" && src?.thumbnail && (
        <img
          className="aia-hl-thumb__img"
          src={src.thumbnail}
          alt=""
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
        />
      )}
    </span>
  );

  const hlCardMeta = (
    h: IProfileHighlight,
    src?: IHlSourceItem
  ): string => {
    if (!src) return "";
    if (h.type === "job")
      return `★ ${src.rating?.toFixed(2) ?? "-"} | ${src.dateRange ?? ""}`;
    if (h.type === "portfolio") {
      const sk = src.skills || [];
      const head = sk.slice(0, 2).join(", ");
      const extra = sk.length > 2 ? `, ${sk.length - 2}+ more` : "";
      return sk.length ? `Skills: ${head}${extra}` : "";
    }
    return src.provider || "";
  };

  // Modal "Save": apply the draft selection locally, persist, close.
  const saveHighlights = async () => {
    const selection = highlightsDraft.slice(0, HL_MAX);
    setAutoBidSettings((prev) => ({
      ...prev,
      autobid_highlights_selection: selection,
    }));
    const ok = await persistHighlights({
      autobid_highlights_selection: selection,
    });
    if (ok) setHighlightsModalOpen(false);
  };

  // Profile state
  const [profileModalOpen, setProfileModalOpen] = useState(false);
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);

  // ── Edit handlers ──
  const startEdit = (id: string, content: string) => {
    setEditingPrompt(id);
    setEditValue(content);
    setPromptError(null);
  };

  const saveEdit = async () => {
    if (!editingPrompt) return;
    const newMatch = editingPrompt === "match" ? editValue : matchPrompt;
    const newRedFlags =
      editingPrompt === "red-flags" ? editValue : redFlagsPrompt;

    // Save to backend FIRST — only update local state / close on success,
    // so a rejected save never looks "saved".
    setPromptSaving(true);
    setPromptError(null);
    try {
      const payload = {
        ...autoBidSettings,
        autobid_match_prompt: newMatch,
        autobid_red_flags_prompt: newRedFlags,
      };
      await settingsApi.updateAutoBidSettings(payload);
      syncAuthWithExtension("AUTOBID_SETTINGS_UPDATE", payload);
      if (editingPrompt === "match") setMatchPrompt(editValue);
      else if (editingPrompt === "red-flags") setRedFlagsPrompt(editValue);
      setEditingPrompt(null);
      setEditValue("");
      notify("success", "Prompt saved");
    } catch (e: any) {
      // Surface the real backend validation message (e.g. the 5000-char limit)
      // instead of a generic error, and keep the editor open to fix it.
      const field =
        editingPrompt === "match"
          ? "autobid_match_prompt"
          : "autobid_red_flags_prompt";
      const backendErrors = e?.response?.data?.errors || e?.errors;
      let message = "Failed to save prompt";
      if (backendErrors && typeof backendErrors === "object") {
        const val = backendErrors[field] ?? Object.values(backendErrors)[0];
        message = Array.isArray(val) ? val[0] : String(val);
      } else if (e?.response?.data?.message) {
        message = e.response.data.message;
      }
      setPromptError(message);
      notify("error", message);
    } finally {
      setPromptSaving(false);
    }
  };

  const cancelEdit = () => {
    setEditingPrompt(null);
    setEditValue("");
    setPromptError(null);
  };

  // ── Profile helpers ──
  const getInitials = (name: string) =>
    name
      .split(" ")
      .map((w) => w[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();

  const getPortraitUrl = (profile: IUpworkProfile): string | null =>
    profile.portrait?.portrait500 ||
    profile.portrait?.bigPortrait ||
    profile.portrait?.portrait ||
    null;

  const formatSyncDate = (date: string | null) => {
    if (!date) return "";
    const d = new Date(date);
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Panel className="ai-settings" title="Settings">
      <p className="aia-page-desc">
        Configure how AI analyzes jobs, writes proposals, and filters
        opportunities for your Upwork profile.
      </p>

      {/* ── Upwork Profile ── */}
      <h2 className="aia-section-title">Default Upwork Profile</h2>
      {!profilesLoading && profiles.length === 0 && (
        <div className="aia-empty aia-empty--inline">
          <div className="aia-empty__avatar">
            <HiOutlineUser size={22} />
          </div>
          <div className="aia-empty__text">
            <h3 className="aia-empty__title">Connect Your Upwork Profile</h3>
            <p className="aia-empty__desc">
              Open your Upwork profile page with the Upwex extension installed
              and click Sync Profile to get started.
            </p>
          </div>
          <a
            href="https://www.upwork.com/freelancers/~"
            target="_blank"
            rel="noopener noreferrer"
            className="aia-empty__action"
          >
            Open Upwork <HiOutlineArrowTopRightOnSquare size={14} />
          </a>
        </div>
      )}

      {/* ── Profile Card (unified: switcher + details) ── */}
      {upworkProfile && (
        <div
          className={`aia-profile-card ${switching ? "aia-profile-card--switching" : ""} ${profileDropdownOpen ? "aia-profile-card--dropdown-open" : ""}`}
        >
          <div
            className={`aia-profile-card__top ${profiles.length > 1 ? "aia-profile-card__top--clickable" : ""}`}
            onClick={() => {
              if (profiles.length > 1)
                setProfileDropdownOpen(!profileDropdownOpen);
            }}
          >
            <div className="aia-profile-card__identity">
              <div className="aia-profile-card__avatar">
                {getPortraitUrl(upworkProfile) ? (
                  <img
                    src={getPortraitUrl(upworkProfile)!}
                    alt={upworkProfile.username}
                  />
                ) : (
                  getInitials(upworkProfile.username)
                )}
              </div>
              <div className="aia-profile-card__name-block">
                <span className="aia-profile-card__name">
                  {upworkProfile.username}
                </span>
                {upworkProfile.profileTitle && (
                  <span className="aia-profile-card__title">
                    {upworkProfile.profileTitle}
                  </span>
                )}
              </div>
            </div>
            <div className="aia-profile-card__actions">
              {currentUser?.sync_start_date && (
                <span className="aia-profile-card__sync-badge">
                  <HiOutlineCheckCircle size={13} />
                  Synced {formatSyncDate(currentUser.sync_start_date)}
                </span>
              )}
              <button
                className="aia-profile-card__details-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setProfileModalOpen(true);
                }}
              >
                Show details
              </button>
              {profiles.length > 1 && (
                <HiOutlineChevronDown
                  size={16}
                  className={`aia-profile-card__chevron ${profileDropdownOpen ? "aia-profile-card__chevron--open" : ""}`}
                />
              )}
            </div>
          </div>

          {/* Profile dropdown (when multiple profiles) */}
          {profileDropdownOpen && profiles.length > 1 && (
            <>
              <div
                className="aia-profile-card__backdrop"
                onClick={() => setProfileDropdownOpen(false)}
              />
              <div className="aia-profile-card__dropdown">
                {profiles.map((p) => (
                  <button
                    key={p.id}
                    className={`aia-profile-card__profile-option ${p.id === activeProfileId ? "aia-profile-card__profile-option--active" : ""}`}
                    onClick={() => handleSwitchProfile(p)}
                    disabled={switching}
                  >
                    <div className="aia-profile-card__profile-avatar">
                      {getInitials(p.username)}
                    </div>
                    <div className="aia-profile-card__profile-info">
                      <span className="aia-profile-card__profile-name">
                        {p.username}
                      </span>
                      <span className="aia-profile-card__profile-title">
                        {p.profileTitle}
                      </span>
                    </div>
                    {p.id === activeProfileId && (
                      <HiOutlineCheckCircle
                        size={16}
                        className="aia-profile-card__profile-check"
                      />
                    )}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Profile details modal */}
      {profileModalOpen && upworkProfile && (
        <div
          className="aia-profile-modal__overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setProfileModalOpen(false);
          }}
        >
          <div className="aia-profile-modal">
            <div className="aia-profile-modal__header">
              <div className="aia-profile-modal__header-identity">
                <div className="aia-profile-card__avatar">
                  {getPortraitUrl(upworkProfile) ? (
                    <img
                      src={getPortraitUrl(upworkProfile)!}
                      alt={upworkProfile.username}
                    />
                  ) : (
                    getInitials(upworkProfile.username)
                  )}
                </div>
                <div className="aia-profile-card__name-block">
                  <span className="aia-profile-card__name">
                    {upworkProfile.username}
                  </span>
                  {upworkProfile.profileTitle && (
                    <span className="aia-profile-card__title">
                      {upworkProfile.profileTitle}
                    </span>
                  )}
                </div>
              </div>
              <button
                className="aia-profile-modal__close"
                onClick={() => setProfileModalOpen(false)}
              >
                <HiOutlineXMark size={20} />
              </button>
            </div>
            <div className="aia-profile-modal__body">
              <div className="aia-profile-card__field">
                <span className="aia-profile-card__label">Description</span>
                <p
                  className="aia-profile-card__value"
                  style={{ whiteSpace: "pre-line" }}
                >
                  {upworkProfile.profileDescription}
                </p>
              </div>
              {upworkProfile.skills.length > 0 && (
                <div className="aia-profile-card__field">
                  <span className="aia-profile-card__label">Skills</span>
                  <div className="aia-profile-card__skills">
                    {upworkProfile.skills.map((s) => (
                      <span key={s} className="aia-profile-card__skill">
                        {s}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              <div className="aia-profile-card__field-row">
                {upworkProfile.country && (
                  <div className="aia-profile-card__field">
                    <span className="aia-profile-card__label">Country</span>
                    <span className="aia-profile-card__value">
                      {upworkProfile.country}
                    </span>
                  </div>
                )}
                <div className="aia-profile-card__field">
                  <span className="aia-profile-card__label">Availability</span>
                  <span className="aia-profile-card__value">
                    {upworkProfile.availability}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Templates ── */}
      <div className="aia-divider" />
      <h2 className="aia-section-title">Auto-Bidding Templates</h2>
      <div className="aia-templates-grid">
        {/* Cover Letter */}
        <div className="aia-tpl-card">
          <div className="aia-tpl-card__top">
            <span className="aia-tpl-card__icon">
              <HiOutlineDocumentText size={16} />
            </span>
            <span className="aia-tpl-card__label">Cover Letter</span>
          </div>
          <div className="aia-tpl-card__desc">
            Choose the default template for Auto-Bidding and Quick Apply. AI
            will use it to generate your proposal automatically.
          </div>
          <div className="aia-tpl-card__footer">
            {!canUseBiddingSettings(currentUser?.plan) ? (
              <span className="aia-tpl-card__locked-status">
                Available on Flex plan.{" "}
                <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
              </span>
            ) : coverTemplatesLoading ? (
              <div
                className="shimmer shimmer--line"
                style={{ width: "100%", height: 36 }}
              />
            ) : coverTemplates.length > 0 ? (
              <select
                className="aia-tpl-card__select"
                value={String(autoBidSettings.autobid_cover_letter_template_id || "")}
                disabled={templateSaving}
                onChange={(e) => {
                  const selectedId = Number(e.target.value);
                  if (selectedId) {
                    setAutoBidSettings((prev) => ({
                      ...prev,
                      autobid_cover_letter_template_id: selectedId,
                    }));
                    setTemplateSaving(true);
                    settingsApi
                      .updateAutoBidSettings({ autobid_cover_letter_template_id: selectedId })
                      .catch(() => {})
                      .finally(() => setTemplateSaving(false));
                  }
                }}
              >
                {coverTemplates.map((t) => (
                  <option key={t.id} value={String(t.id)}>
                    {t.name}
                  </option>
                ))}
              </select>
            ) : (
              <button
                className="aia-tpl-card__change-btn"
                onClick={() => navigate(APP_ROUTES.aiTemplates)}
              >
                Create Template
              </button>
            )}
          </div>
        </div>

        {/* Match Algorithm */}
        <div className="aia-tpl-card">
          <div className="aia-tpl-card__top">
            <span className="aia-tpl-card__icon aia-tpl-card__icon--blue">
              <HiOutlineShieldCheck size={16} />
            </span>
            <span className="aia-tpl-card__label">Match Algorithm</span>
          </div>
          <div className="aia-tpl-card__desc">
            Rules used by Auto-Bidding to score every job from 0 to 100 based on
            how well it matches your skills, budget, and preferences. Higher
            score = better fit.
          </div>
          <div className="aia-tpl-card__footer">
            {canUseAutoBid(currentUser?.plan) ? (
              <button
                className="aia-tpl-card__change-btn"
                onClick={() => startEdit("match", matchPrompt)}
              >
                Change
              </button>
            ) : (
              <span className="aia-tpl-card__locked-status">
                Available on Flex plan.{" "}
                <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
              </span>
            )}
          </div>
        </div>

        {/* Red Flags */}
        <div className="aia-tpl-card">
          <div className="aia-tpl-card__top">
            <span className="aia-tpl-card__icon aia-tpl-card__icon--orange">
              <HiOutlineExclamationTriangle size={16} />
            </span>
            <span className="aia-tpl-card__label">Red Flags</span>
          </div>
          <div className="aia-tpl-card__desc">
            Filters used by Auto-Bidding to automatically skip bad jobs like
            unverified clients, low budgets, and spam posts. Saves your connects
            from being wasted.
          </div>
          <div className="aia-tpl-card__footer">
            {canUseAutoBid(currentUser?.plan) ? (
              <button
                className="aia-tpl-card__change-btn"
                onClick={() => startEdit("red-flags", redFlagsPrompt)}
              >
                Change
              </button>
            ) : (
              <span className="aia-tpl-card__locked-status">
                Available on Flex plan.{" "}
                <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ── Profile Highlights ── */}
      <div className="aia-divider" />
      <h2 className="aia-section-title">Profile Highlights</h2>
      <p className="aia-section-desc">
        Add up to 4 relevant items from your profile – Upwork jobs, portfolio
        projects, or certificates – to every proposal. Let AI choose the best
        fit for each job, or curate them yourself. Applied during Autocomplete,
        Manual Auto Bid, and Auto-Bidding.
      </p>

      <div className="aia-ab">
        {!canUseBiddingSettings(currentUser?.plan) ? (
          <div className="aia-ab__row">
            <div className="aia-ab__text">
              <span className="aia-ab__label">Profile Highlights</span>
              <span className="aia-ab__desc">
                Works with Autocomplete, Manual Auto Bid, and Auto-Bidding.
              </span>
            </div>
            <span className="aia-ab__locked-status">
              Available on Flex plan.{" "}
              <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
            </span>
          </div>
        ) : (
          <>
            <div className="aia-ab__row">
              <div className="aia-ab__text">
                <span className="aia-ab__label">Highlights</span>
                <span className="aia-ab__desc">
                  How highlights are added to your proposals
                </span>
              </div>
              <div className="aia-ab__control">
                <select
                  className="aia-ab__select"
                  value={autoBidSettings.autobid_highlights_mode}
                  disabled={highlightsLoading || !hlHasAvailable}
                  onChange={(e) =>
                    setHighlightsMode(
                      e.target
                        .value as IAutoBidSettings["autobid_highlights_mode"]
                    )
                  }
                >
                  <option value="off">Off</option>
                  <option value="manual">Manual</option>
                  <option value="ai">Auto (AI)</option>
                </select>
              </div>
            </div>

            {!highlightsLoading && !hlHasAvailable && (
              <div className="aia-hl-warn">
                <HiOutlineExclamationTriangle size={16} />
                <span>
                  No highlights available yet. Even if your profile is already
                  synced, this feature needs a fresh re-sync: open your Upwork
                  freelancer profile with the Upwex extension active and click{" "}
                  <b>Update Profile</b> - we'll pull your completed jobs,
                  portfolio projects, and certificates.
                </span>
              </div>
            )}

            {hlHasAvailable &&
              autoBidSettings.autobid_highlights_mode === "ai" && (
              <div className="aia-hl-ai-note">
                <HiOutlineSparkles size={16} />
                <span>
                  AI analyzes each job and automatically picks up to 4 of the
                  most relevant highlights from your profile - completed jobs,
                  portfolio projects, and certificates. No manual selection
                  needed: the best-fit items are added to every proposal for
                  you. Uses 1 additional AI credit per proposal.
                </span>
              </div>
            )}

            {hlHasAvailable &&
              autoBidSettings.autobid_highlights_mode === "manual" && (
              <div className="aia-hl-manual">
                <div className="aia-hl-manual__head">
                  <span className="aia-hl-manual__count">
                    {(autoBidSettings.autobid_highlights_selection || []).length}{" "}
                    of 4 selected
                  </span>
                  <button
                    className="aia-hl-edit-btn"
                    onClick={openHighlightsModal}
                  >
                    {(autoBidSettings.autobid_highlights_selection || []).length >
                    0
                      ? "Edit selection"
                      : "Select highlights"}
                  </button>
                </div>

                {(autoBidSettings.autobid_highlights_selection || []).length >
                0 ? (
                  <div className="aia-hl-preview">
                    {autoBidSettings.autobid_highlights_selection.map((h, i) => {
                      const src = findHlSource(h.type, h);
                      const unavailable = isHlUnavailable(h);
                      return (
                        <div
                          key={hlKey(h)}
                          className={`aia-hl-card ${
                            unavailable ? "is-unavailable" : ""
                          }`}
                        >
                          {renderHlThumb(h.type, src)}
                          <div className="aia-hl-card__body">
                            <span className="aia-hl-card__type">
                              {HL_TYPE_LABEL[h.type]}
                              {unavailable && (
                                <span className="aia-hl-card__unavailable">
                                  Unavailable
                                </span>
                              )}
                            </span>
                            <span className="aia-hl-card__title">
                              {i + 1} - {h.title}
                            </span>
                            {!!hlCardMeta(h, src) && (
                              <span className="aia-hl-card__meta">
                                {hlCardMeta(h, src)}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <span className="aia-hl-manual__empty">
                    No highlights selected yet.
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Smart Boosting ── ВІДКЛАДЕНО (не завершено), секція закоментована
      <div className="aia-divider" />
      <h2 className="aia-section-title">Smart Boosting</h2>
      <p className="aia-section-desc">
        Let AI decide when a job is worth boosting with Connects.
      </p>

      <div className="aia-ab">
        {!canUseBiddingSettings(currentUser?.plan) ? (
          <div className="aia-ab__row">
            <div className="aia-ab__text">
              <span className="aia-ab__label">Smart Boosting</span>
              <span className="aia-ab__desc">
                AI places a boost bid only when the job is worth it.
              </span>
            </div>
            <span className="aia-ab__locked-status">
              Available on Flex plan.{" "}
              <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
            </span>
          </div>
        ) : (
          <>
            <div className="aia-ab__row">
              <div className="aia-ab__text">
                <span className="aia-ab__label">Boosting</span>
                <span className="aia-ab__desc">
                  How boost bids are placed on your proposals
                </span>
              </div>
              <div className="aia-ab__control">
                <label className="aia-ab__toggle">
                  <input
                    type="checkbox"
                    checked={autoBidSettings.autobid_smart_boost}
                    onChange={(e) => setSmartBoost(e.target.checked)}
                  />
                  <span className="aia-ab__toggle-slider" />
                </label>
              </div>
            </div>

            {autoBidSettings.autobid_smart_boost && (
              <>
                <div className="aia-ab__row">
                  <div className="aia-ab__text">
                    <span className="aia-ab__label">
                      Max Connects per Smart Bid
                    </span>
                    <span className="aia-ab__desc">
                      Upper limit for a single AI boost bid (1-1000)
                    </span>
                  </div>
                  <div className="aia-ab__control">
                    <input
                      type="number"
                      className="aia-ab__number"
                      min={1}
                      max={1000}
                      value={autoBidSettings.autobid_smart_boost_max_connects}
                      onChange={(e) =>
                        updateAB(
                          "autobid_smart_boost_max_connects",
                          Number(e.target.value)
                        )
                      }
                      onBlur={persistSmartBoostMax}
                    />
                  </div>
                </div>

                <div className="aia-hl-ai-note">
                  <HiOutlineSparkles size={16} />
                  <span>
                    For each proposal, AI reviews the boost auction - current
                    bids, Upwork's suggested bid, and your Connects balance -
                    and places a boost bid only when the job is worth it. Uses
                    1 additional AI credit per proposal.
                  </span>
                </div>
              </>
            )}
          </>
        )}
      </div>
      */}

      {/* ── Auto-Bidding & Quick Apply ── */}
      <div className="aia-divider" />
      <h2 className="aia-section-title">Auto-Bidding & Quick Apply</h2>
      <p className="aia-section-desc">
        Configure how AI fills out proposals: your rate, payment terms, and bid
        adjustments for automatic and one-click applications.
      </p>

      <div className="aia-ab">
        {abSettingsLoading ? (
          <>
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="aia-ab__row">
                <div className="aia-ab__text">
                  <div
                    className="shimmer shimmer--line"
                    style={{ width: "50%", height: 14 }}
                  />
                  <div
                    className="shimmer shimmer--line"
                    style={{ width: "80%", height: 10, marginTop: 6 }}
                  />
                </div>
                <div
                  className="shimmer shimmer--line"
                  style={{ width: 200, height: 36 }}
                />
              </div>
            ))}
          </>
        ) : (
          <>
            <div className="aia-ab__group-title">Proposal Settings</div>
            {/* Hourly Rate */}
            <div className={`aia-ab__row ${abErrors.autobid_hourly_rate ? "aia-ab__row--error" : ""}`}>
              <div className="aia-ab__text">
                <span className="aia-ab__label">Hourly Rate</span>
                <span className="aia-ab__desc">
                  From your Upwork profile rate
                </span>
                {abErrors.autobid_hourly_rate && (
                  <span className="aia-ab__error">{abErrors.autobid_hourly_rate}</span>
                )}
              </div>
              {canUseBiddingSettings(currentUser?.plan) ? (
                <div className={`aia-ab__control aia-ab__input-wrap ${abErrors.autobid_hourly_rate ? "aia-ab__input-wrap--error" : ""}`}>
                  <span className="aia-ab__input-prefix">$</span>
                  <input
                    type="number"
                    className="aia-ab__number"
                    min={1}
                    value={autoBidSettings.autobid_hourly_rate}
                    onChange={(e) =>
                      updateAB("autobid_hourly_rate", Number(e.target.value))
                    }
                  />
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>

            {/* Rate Increase */}
            <div className="aia-ab__row">
              <div className="aia-ab__text">
                <span className="aia-ab__label">Rate Increase</span>
                <span className="aia-ab__desc">
                  Request periodic rate increases on hourly contracts
                </span>
              </div>
              {canUseBiddingSettings(currentUser?.plan) ? (
                <div className="aia-ab__control aia-ab__control--pair">
                  <select
                    className="aia-ab__select"
                    value={autoBidSettings.autobid_rate_increase_frequency}
                    onChange={(e) =>
                      updateAB("autobid_rate_increase_frequency", e.target.value)
                    }
                  >
                    <option value="never">Never</option>
                    <option value="3months">Every 3 months</option>
                    <option value="6months">Every 6 months</option>
                    <option value="12months">Every 12 months</option>
                  </select>
                  {autoBidSettings.autobid_rate_increase_frequency !==
                    "never" && (
                    <select
                      className="aia-ab__select aia-ab__select--narrow"
                      value={autoBidSettings.autobid_rate_increase_percentage}
                      onChange={(e) =>
                        updateAB(
                          "autobid_rate_increase_percentage",
                          Number(e.target.value)
                        )
                      }
                    >
                      <option value={5}>5%</option>
                      <option value={10}>10%</option>
                      <option value={15}>15%</option>
                    </select>
                  )}
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>

            <div className="aia-ab__group-title">Bid Configuration</div>

            {/* Fixed Price Adjustment */}
            <div className="aia-ab__row">
              <div className="aia-ab__text">
                <span className="aia-ab__label">Bid Adjustment</span>
                <span className="aia-ab__desc">
                  Adjust the client's budget up or down for fixed-price bids
                </span>
              </div>
              {canUseBiddingSettings(currentUser?.plan) ? (
                <div className="aia-ab__control">
                  <select
                    className="aia-ab__select"
                    value={autoBidSettings.autobid_fixed_price_adjustment}
                    onChange={(e) =>
                      updateAB(
                        "autobid_fixed_price_adjustment",
                        Number(e.target.value)
                      )
                    }
                  >
                    <option value={-20}>-20%</option>
                    <option value={-10}>-10%</option>
                    <option value={0}>No change</option>
                    <option value={10}>+10%</option>
                    <option value={20}>+20%</option>
                  </select>
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>

            {/* Project Duration */}
            <div className="aia-ab__row">
              <div className="aia-ab__text">
                <span className="aia-ab__label">Project Duration</span>
                <span className="aia-ab__desc">
                  Auto reads duration from the job listing
                </span>
              </div>
              {canUseBiddingSettings(currentUser?.plan) ? (
                <div className="aia-ab__control">
                  <select
                    className="aia-ab__select"
                    value={autoBidSettings.autobid_project_duration}
                    onChange={(e) =>
                      updateAB("autobid_project_duration", e.target.value)
                    }
                  >
                    <option value="automatic">Auto</option>
                    <option value="Less than 1 month">Less than 1 month</option>
                    <option value="1 to 3 months">1 to 3 months</option>
                    <option value="3 to 6 months">3 to 6 months</option>
                    <option value="More than 6 months">More than 6 months</option>
                  </select>
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>

            <div className="aia-ab__group-title">Safety Limits</div>
            <div className={`aia-ab__row ${abErrors.autobid_max_bids_per_day ? "aia-ab__row--error" : ""}`}>
              <div className="aia-ab__text">
                <span className="aia-ab__label">Max Bids Per Day</span>
                <span className="aia-ab__desc">Auto-stop after this many proposals per day</span>
                {abErrors.autobid_max_bids_per_day && (
                  <span className="aia-ab__error">{abErrors.autobid_max_bids_per_day}</span>
                )}
              </div>
              {canUseAutoBid(currentUser?.plan) ? (
                <div className="aia-ab__control">
                  <input type="number" className={`aia-ab__number ${abErrors.autobid_max_bids_per_day ? "aia-ab__number--error" : ""}`} min={1} max={100} value={autoBidSettings.autobid_max_bids_per_day} onChange={(e) => updateAB("autobid_max_bids_per_day", Number(e.target.value))} />
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>
            <div className={`aia-ab__row ${abErrors.autobid_max_connects_per_day ? "aia-ab__row--error" : ""}`}>
              <div className="aia-ab__text">
                <span className="aia-ab__label">Max Connects Per Day</span>
                <span className="aia-ab__desc">Daily connects budget limit</span>
                {abErrors.autobid_max_connects_per_day && (
                  <span className="aia-ab__error">{abErrors.autobid_max_connects_per_day}</span>
                )}
              </div>
              {canUseAutoBid(currentUser?.plan) ? (
                <div className="aia-ab__control">
                  <input type="number" className={`aia-ab__number ${abErrors.autobid_max_connects_per_day ? "aia-ab__number--error" : ""}`} min={10} max={1000} value={autoBidSettings.autobid_max_connects_per_day} onChange={(e) => updateAB("autobid_max_connects_per_day", Number(e.target.value))} />
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>
            <div className={`aia-ab__row ${abErrors.autobid_stop_on_low_connects ? "aia-ab__row--error" : ""}`}>
              <div className="aia-ab__text">
                <span className="aia-ab__label">Stop on Low Connects</span>
                <span className="aia-ab__desc">Pause scanning when available connects drop below this</span>
                {abErrors.autobid_stop_on_low_connects && (
                  <span className="aia-ab__error">{abErrors.autobid_stop_on_low_connects}</span>
                )}
              </div>
              {canUseAutoBid(currentUser?.plan) ? (
                <div className="aia-ab__control">
                  <input type="number" className={`aia-ab__number ${abErrors.autobid_stop_on_low_connects ? "aia-ab__number--error" : ""}`} min={0} max={500} value={autoBidSettings.autobid_stop_on_low_connects} onChange={(e) => updateAB("autobid_stop_on_low_connects", Number(e.target.value))} />
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>

            <div className="aia-ab__group-title">Scan Behavior</div>
            <div className="aia-ab__row">
              <div className="aia-ab__text">
                <span className="aia-ab__label">Scan Interval</span>
                <span className="aia-ab__desc">Pause between scan cycles</span>
              </div>
              {canUseAutoBid(currentUser?.plan) ? (
                <div className="aia-ab__control">
                  <select className="aia-ab__select" value={autoBidSettings.autobid_scan_interval} onChange={(e) => updateAB("autobid_scan_interval", e.target.value)}>
                    <option value="fast">Fast (2-4 min)</option>
                    <option value="normal">Normal (3-7 min)</option>
                    <option value="slow">Slow (5-10 min)</option>
                  </select>
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>
            <div className="aia-ab__row">
              <div className="aia-ab__text">
                <span className="aia-ab__label">Jobs Per Page</span>
                <span className="aia-ab__desc">Number of jobs to scan per cycle</span>
              </div>
              {canUseAutoBid(currentUser?.plan) ? (
                <div className="aia-ab__control">
                  <select className="aia-ab__select" value={autoBidSettings.autobid_jobs_per_page} onChange={(e) => updateAB("autobid_jobs_per_page", Number(e.target.value))}>
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                  </select>
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>
            <div className="aia-ab__row">
              <div className="aia-ab__text">
                <span className="aia-ab__label">Job Age Limit</span>
                <span className="aia-ab__desc">Only consider jobs posted within this timeframe</span>
              </div>
              {canUseAutoBid(currentUser?.plan) ? (
                <div className="aia-ab__control">
                  <select className="aia-ab__select" value={autoBidSettings.autobid_job_age_limit} onChange={(e) => updateAB("autobid_job_age_limit", e.target.value)}>
                    <option value="1h">Last hour</option>
                    <option value="6h">Last 6 hours</option>
                    <option value="24h">Last 24 hours</option>
                    <option value="3d">Last 3 days</option>
                    <option value="7d">Last 7 days</option>
                    <option value="any">Any age</option>
                  </select>
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>

            <div className="aia-ab__group-title">Bid Preferences</div>
            <div className={`aia-ab__row ${abErrors.autobid_min_match_score ? "aia-ab__row--error" : ""}`}>
              <div className="aia-ab__text">
                <span className="aia-ab__label">Min Match Score</span>
                <span className="aia-ab__desc">AI must score the job at least this high to bid (40-90)</span>
                {abErrors.autobid_min_match_score && (
                  <span className="aia-ab__error">{abErrors.autobid_min_match_score}</span>
                )}
              </div>
              {canUseAutoBid(currentUser?.plan) ? (
                <div className="aia-ab__control">
                  <input type="number" className={`aia-ab__number ${abErrors.autobid_min_match_score ? "aia-ab__number--error" : ""}`} min={40} max={90} value={autoBidSettings.autobid_min_match_score} onChange={(e) => updateAB("autobid_min_match_score", Number(e.target.value))} />
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>
            <div className="aia-ab__row">
              <div className="aia-ab__text">
                <span className="aia-ab__label">Skip Fixed-Price Jobs</span>
                <span className="aia-ab__desc">Only bid on hourly jobs</span>
              </div>
              {canUseAutoBid(currentUser?.plan) ? (
                <div className="aia-ab__control">
                  <label className="aia-ab__toggle">
                    <input type="checkbox" checked={autoBidSettings.autobid_skip_fixed_price} onChange={(e) => updateAB("autobid_skip_fixed_price", e.target.checked)} />
                    <span className="aia-ab__toggle-slider" />
                  </label>
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>
            <div className="aia-ab__row">
              <div className="aia-ab__text">
                <span className="aia-ab__label">Skip Hourly Jobs</span>
                <span className="aia-ab__desc">Only bid on fixed-price jobs</span>
              </div>
              {canUseAutoBid(currentUser?.plan) ? (
                <div className="aia-ab__control">
                  <label className="aia-ab__toggle">
                    <input type="checkbox" checked={autoBidSettings.autobid_skip_hourly} onChange={(e) => updateAB("autobid_skip_hourly", e.target.checked)} />
                    <span className="aia-ab__toggle-slider" />
                  </label>
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>

            <div className="aia-ab__group-title">Schedule</div>
            <div className={`aia-ab__row ${abErrors.autobid_active_hours_from || abErrors.autobid_active_hours_to ? "aia-ab__row--error" : ""}`}>
              <div className="aia-ab__text">
                <span className="aia-ab__label">Active Hours</span>
                <span className="aia-ab__desc">Only scan during these hours (leave empty for 24/7)</span>
                {(abErrors.autobid_active_hours_from || abErrors.autobid_active_hours_to) && (
                  <span className="aia-ab__error">{abErrors.autobid_active_hours_from || abErrors.autobid_active_hours_to}</span>
                )}
              </div>
              {canUseAutoBid(currentUser?.plan) ? (
                <div className="aia-ab__control aia-ab__control--pair">
                  <input ref={timeFromRef} type="time" className={`aia-ab__time ${abErrors.autobid_active_hours_from ? "aia-ab__time--error" : ""}`} value={autoBidSettings.autobid_active_hours_from || ""} onChange={(e) => updateAB("autobid_active_hours_from", e.target.value || null)} />
                  <span className="aia-ab__time-sep">to</span>
                  <input ref={timeToRef} type="time" className={`aia-ab__time ${abErrors.autobid_active_hours_to ? "aia-ab__time--error" : ""}`} value={autoBidSettings.autobid_active_hours_to || ""} onChange={(e) => updateAB("autobid_active_hours_to", e.target.value || null)} />
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>
            <div className="aia-ab__row">
              <div className="aia-ab__text">
                <span className="aia-ab__label">Timezone</span>
                <span className="aia-ab__desc">Used for active hours schedule</span>
              </div>
              {canUseAutoBid(currentUser?.plan) ? (
                <div className="aia-ab__control">
                  <select className="aia-ab__select" value={autoBidSettings.autobid_timezone || ""} onChange={(e) => updateAB("autobid_timezone", e.target.value || null)}>
                    <option value="">Auto (browser)</option>
                    <option value="America/New_York">Eastern (New York)</option>
                    <option value="America/Chicago">Central (Chicago)</option>
                    <option value="America/Denver">Mountain (Denver)</option>
                    <option value="America/Los_Angeles">Pacific (Los Angeles)</option>
                    <option value="Europe/London">London (GMT)</option>
                    <option value="Europe/Berlin">Berlin (CET)</option>
                    <option value="Europe/Kiev">Kyiv (EET)</option>
                    <option value="Asia/Dubai">Dubai (GST)</option>
                    <option value="Asia/Kolkata">India (IST)</option>
                    <option value="Asia/Shanghai">China (CST)</option>
                    <option value="Asia/Tokyo">Tokyo (JST)</option>
                    <option value="Australia/Sydney">Sydney (AEST)</option>
                  </select>
                </div>
              ) : (
                <span className="aia-ab__locked-status">
                  Available on Flex plan.{" "}
                  <a onClick={() => navigate(APP_ROUTES.plans)}>Upgrade</a>
                </span>
              )}
            </div>

            {/* Save */}
            {canUseBiddingSettings(currentUser?.plan) && (
              <div className="aia-ab__footer">
                <FormButton
                  theme="green-light"
                  onClick={handleSaveAutoBidSettings}
                  disabled={abSettingsSaving}
                >
                  {abSettingsSaving ? "Saving..." : "Save Settings"}
                </FormButton>
              </div>
            )}
          </>
        )}
      </div>

      {/* Edit Modals */}
      {(editingPrompt === "match" || editingPrompt === "red-flags") && (
        <div className="aia-modal-overlay" onMouseDown={(e) => {
            if (e.target === e.currentTarget) cancelEdit();
          }}>
          <div className="aia-modal">
            <div className="aia-modal__header">
              <h3 className="aia-modal__title">
                {editingPrompt === "match"
                  ? "Edit Match Algorithm"
                  : "Edit Red Flags"}
              </h3>
              <button className="aia-modal__close" onClick={() => cancelEdit()}>
                <HiOutlineXMark size={20} />
              </button>
            </div>
            <p className="aia-modal__desc">
              {editingPrompt === "match"
                ? "Define scoring criteria that AI uses to evaluate job-profile fit."
                : "Define rules to skip or flag low-quality jobs before they reach you."}
            </p>
            <textarea
              className={`aia-modal__textarea ${editValue.length > PROMPT_MAX_LENGTH ? "aia-modal__textarea--error" : ""}`}
              value={editValue}
              onChange={(e) => {
                setEditValue(e.target.value);
                if (promptError) setPromptError(null);
              }}
              rows={14}
              autoFocus
            />
            <div className="aia-modal__meta">
              {promptError ? (
                <span className="aia-modal__error">{promptError}</span>
              ) : (
                <span />
              )}
              <span
                className={`aia-modal__counter ${editValue.length > PROMPT_MAX_LENGTH ? "aia-modal__counter--error" : ""}`}
              >
                {editValue.length.toLocaleString()} /{" "}
                {PROMPT_MAX_LENGTH.toLocaleString()}
              </span>
            </div>
            <div className="aia-modal__actions">
              <FormButton theme="grey" onClick={() => cancelEdit()}>
                Cancel
              </FormButton>
              <FormButton
                theme="green-light"
                onClick={saveEdit}
                disabled={
                  promptSaving || editValue.length > PROMPT_MAX_LENGTH
                }
              >
                {promptSaving ? "Saving..." : "Save"}
              </FormButton>
            </div>
          </div>
        </div>
      )}

      {/* Profile Highlights — manual picker */}
      {highlightsModalOpen && (
        <div
          className="aia-modal-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setHighlightsModalOpen(false);
          }}
        >
          <div className="aia-modal aia-hl-modal">
            <div className="aia-modal__header">
              <h3 className="aia-modal__title">Add profile highlights</h3>
              <button
                className="aia-modal__close"
                onClick={() => setHighlightsModalOpen(false)}
              >
                <HiOutlineXMark size={20} />
              </button>
            </div>
            <p className="aia-modal__desc">
              Select up to {HL_MAX} highlights to add to your proposals, in the
              order they should appear.
            </p>

            <div className="aia-hl-body">
              {/* Left: source items by tab */}
              <div className="aia-hl-left">
                <div className="aia-hl-tabs">
                  {HL_TABS.map((t) => (
                    <button
                      key={t.key}
                      className={`aia-hl-tab ${
                        highlightsTab === t.key ? "is-active" : ""
                      }`}
                      onClick={() => setHighlightsTab(t.key)}
                    >
                      {t.label} ({highlightsSource[t.key].length})
                    </button>
                  ))}
                </div>
                <div className="aia-hl-list">
                  {highlightsSource[highlightsTab].length === 0 ? (
                    <div className="aia-hl-empty">Nothing here yet.</div>
                  ) : (
                    highlightsSource[highlightsTab].map((item) => {
                      const selected = isHighlightSelected(
                        highlightsTab,
                        item
                      );
                      const full =
                        highlightsDraft.length >= HL_MAX && !selected;
                      return (
                        <div
                          key={item.id}
                          className={`aia-hl-item ${
                            selected ? "is-selected" : ""
                          }`}
                        >
                          {renderHlThumb(highlightsTab, item)}
                          <div className="aia-hl-item__body">
                            <div className="aia-hl-item__title">
                              {item.title}
                            </div>

                            {highlightsTab === "job" && (
                              <div className="aia-hl-item__meta">
                                <span className="aia-hl-star">★</span>{" "}
                                {item.rating?.toFixed(1)} · {item.dateRange} ·{" "}
                                {item.price}
                              </div>
                            )}
                            {highlightsTab === "certificate" && (
                              <div className="aia-hl-item__meta">
                                Provider: {item.provider} · Issued:{" "}
                                {item.issued}
                              </div>
                            )}
                            {highlightsTab === "portfolio" &&
                              !!item.skills?.length && (
                                <div className="aia-hl-tokens">
                                  {item.skills.slice(0, 4).map((s) => (
                                    <span key={s} className="aia-hl-token">
                                      {s}
                                    </span>
                                  ))}
                                  {item.skills.length > 4 && (
                                    <span className="aia-hl-token">
                                      +{item.skills.length - 4} more
                                    </span>
                                  )}
                                </div>
                              )}

                            {item.description && (
                              <p className="aia-hl-item__desc">
                                {item.description}
                              </p>
                            )}

                            {selected ? (
                              <button
                                className="aia-hl-remove-btn"
                                onClick={() =>
                                  toggleHighlight(highlightsTab, item)
                                }
                              >
                                <HiOutlineTrash size={14} /> Remove
                              </button>
                            ) : (
                              <button
                                className="aia-hl-select-btn"
                                disabled={full}
                                onClick={() =>
                                  toggleHighlight(highlightsTab, item)
                                }
                              >
                                Select highlight
                              </button>
                            )}
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* Right: selected highlights (ordered, draggable) */}
              <div className="aia-hl-right">
                <h4 className="aia-hl-right__title">
                  Highlights ({highlightsDraft.length}/{HL_MAX})
                </h4>
                {highlightsDraft.length === 0 ? (
                  <div className="aia-hl-empty">No highlights selected</div>
                ) : (
                  <div className="aia-hl-cards">
                    {highlightsDraft.map((h, i) => {
                      const src = findHlSource(h.type, h);
                      const unavailable = isHlUnavailable(h);
                      return (
                        <div
                          key={hlKey(h)}
                          className={`aia-hl-card ${
                            hlDragIndex === i ? "is-dragging" : ""
                          } ${unavailable ? "is-unavailable" : ""}`}
                          draggable
                          onDragStart={() => setHlDragIndex(i)}
                          onDragOver={(e) => e.preventDefault()}
                          onDrop={() => {
                            if (hlDragIndex !== null)
                              moveHighlight(hlDragIndex, i);
                            setHlDragIndex(null);
                          }}
                          onDragEnd={() => setHlDragIndex(null)}
                        >
                          <span className="aia-hl-card__handle" aria-hidden>
                            ⠿
                          </span>
                          {renderHlThumb(h.type, src)}
                          <div className="aia-hl-card__body">
                            <span className="aia-hl-card__type">
                              {HL_TYPE_LABEL[h.type]}
                              {unavailable && (
                                <span className="aia-hl-card__unavailable">
                                  Unavailable
                                </span>
                              )}
                            </span>
                            <span className="aia-hl-card__title">
                              {i + 1} - {h.title}
                            </span>
                            {!!hlCardMeta(h, src) && (
                              <span className="aia-hl-card__meta">
                                {hlCardMeta(h, src)}
                              </span>
                            )}
                          </div>
                          <button
                            className="aia-hl-card__del"
                            title="Remove"
                            onClick={() => removeHighlight(h)}
                          >
                            <HiOutlineTrash size={16} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            <div className="aia-modal__actions">
              <FormButton
                theme="grey"
                onClick={() => setHighlightsModalOpen(false)}
              >
                Cancel
              </FormButton>
              <FormButton
                theme="green-light"
                onClick={saveHighlights}
                disabled={highlightsSaving}
              >
                {highlightsSaving ? "Saving..." : "Save"}
              </FormButton>
            </div>
          </div>
        </div>
      )}
    </Panel>
  );
};

export default AISettings;
